import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
	Box,
	Checkbox,
	Dialog,
	FormControlLabel,
	IconButton,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import TableRowsIcon from '@mui/icons-material/TableRows'
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline'
import { type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid-pro'
import { useModal } from '../../../../../store/ModalProvider'
import CustomButton from '../../CustomButton/CustomButton'
import { AnimatedDataGridPro } from '../../CustomDataGrid'
import { dataGridDensityTransitionSx } from '../../CustomDataGrid/dataGridDensityTransitionSx'
import CustomColumnMenu, { columnMenuPopperSx } from '../../CustomDataGrid/CustomColumnMenu'
import { popupActionButtonSx, popupSecondaryButtonSx } from '../popupButtonStyles'
import { popupInputLabelSx } from '../popupInputStyles'
import type { ScheduleEmailsModalData } from '../../../../../types/modal'

const dialogShadow =
	'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)'

const checkboxSx = {
	color: 'rgba(134, 149, 175, 0.5)',
	'&.Mui-checked': {
		color: '#00A5FF',
	},
	'& .MuiSvgIcon-root': {
		fontSize: '18px',
	},
}

const checkboxLabelSx = {
	margin: 0,
	'& .MuiFormControlLabel-label': {
		marginTop: '1px',
	},
	'.MuiTypography-root': {
		fontFamily: 'Poppins, sans-serif',
		fontSize: '13px',
		fontWeight: 500,
		lineHeight: 1.3,
		color: '#092056',
	},
}

const outlinedFieldSx = {
	'& .MuiInputLabel-root': {
		...popupInputLabelSx,
	},
	'& .MuiOutlinedInput-root': {
		height: '40px',
		backgroundColor: '#FFFFFF',
		borderRadius: '4px',
		fontSize: '13px',
		fontWeight: 500,
		fontFamily: 'Poppins, sans-serif',
		color: '#092056',
		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0.32)',
		},
	},
	'& .MuiOutlinedInput-input': {
		padding: '10px 12px',
		'&::placeholder': {
			color: '#092056',
			opacity: 1,
		},
	},
}

const toggleGroupSx = {
	border: '1px solid rgba(134, 149, 175, 0.15)',
	borderRadius: '4px',
	height: '24px',
	'& .MuiToggleButton-root': {
		border: 'none',
		borderRadius: 0,
		padding: '4.8px',
		width: '24px',
		height: '24px',
		minWidth: '24px',
		color: '#00A5FF',
		'&.Mui-selected': {
			backgroundColor: '#F5F7FC',
			color: '#00A5FF',
		},
		'&:hover': {
			backgroundColor: 'rgba(0, 165, 255, 0.08)',
		},
	},
	'& .MuiToggleButton-root:not(:last-of-type)': {
		borderRight: '1px solid rgba(134, 149, 175, 0.15)',
	},
}

const addLinkSx = {
	display: 'inline-flex',
	alignItems: 'center',
	gap: '6px',
	padding: 0,
	minWidth: 'auto',
	fontFamily: 'Poppins, sans-serif',
	fontSize: '13px',
	fontWeight: 600,
	color: '#7FD7FF',
	textTransform: 'none',
	'&:hover': {
		backgroundColor: 'transparent',
	},
}

const inlineActionSx = {
	padding: 0,
	minWidth: 'auto',
	fontFamily: 'Poppins, sans-serif',
	fontSize: '16px',
	fontWeight: 600,
	lineHeight: 1.2,
	color: '#00A5FF',
	textTransform: 'none',
	'&:hover': {
		backgroundColor: 'transparent',
	},
}

const listLabelSx = {
	fontSize: '11px',
	fontWeight: 600,
	color: '#9BB0D7',
	letterSpacing: '0.15px',
	fontFamily: 'Poppins, sans-serif',
}

const emailLineSx = {
	fontSize: '12px',
	fontWeight: 500,
	color: '#092056',
	lineHeight: 1.6,
	fontFamily: 'Poppins, sans-serif',
	wordBreak: 'break-all',
}

type EmailRow = {
	id: string
	recipient: string
	ccRecipient: string
	isRecipientPlaceholder: boolean
	isCcPlaceholder: boolean
}

const tableSx = {
	border: 'none',
	'--DataGrid-rowBorderColor': 'transparent',
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: '#F5F7FC',
		borderBottom: '1px solid rgba(155, 176, 215, 0.2)',
		borderTop: 'none',
		boxShadow: 'none',
	},
	'& .MuiDataGrid-columnHeaderTitle': {
		...listLabelSx,
	},
	'& .MuiDataGrid-columnSeparator': {
		opacity: 0,
		color: 'rgba(134, 149, 175, 0.6)',
		transition: 'opacity 0.2s ease',
	},
	'& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
		opacity: 1,
	},
	'& .MuiDataGrid-columnSeparator--resizable': {
		cursor: 'col-resize',
	},
	'& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
		borderRight: 'none',
	},
	'& .MuiDataGrid-cell': {
		...emailLineSx,
		borderBottom: 'none',
	},
	'& .MuiDataGrid-row': {
		backgroundColor: '#FFFFFF',
	},
	'& .MuiDataGrid-row.email-row-odd': {
		backgroundColor: 'rgba(245, 247, 252, 0.5)',
	},
	'& .MuiDataGrid-row.email-row-enter': {
		animation: 'emailRowEnter 260ms ease-out',
	},
	'& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
		outline: 'none',
	},
	'@keyframes emailRowEnter': {
		from: {
			opacity: 0,
			transform: 'translateY(6px)',
		},
		to: {
			opacity: 1,
			transform: 'translateY(0)',
		},
	},
}

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

const collapseDurationMs = 240
const collapseEnterEasing = 'cubic-bezier(0.2, 0, 0, 1)'
const collapseExitEasing = 'cubic-bezier(0.4, 0, 1, 1)'
const getCollapseTransition = (easing: string) =>
	`height ${collapseDurationMs}ms ${easing}, opacity ${collapseDurationMs}ms ${easing}, transform ${collapseDurationMs}ms ${easing}`

const CustomModalScheduleEmails: React.FC = () => {
	const { isOpen, closeModal, modalData, modalHandlers } = useModal()
	const data = modalData as ScheduleEmailsModalData | undefined
	const [handleSave] = modalHandlers ?? []

	const [sendingDisabled, setSendingDisabled] = useState(false)
	const [viewMode, setViewMode] = useState<'table' | 'list'>('table')
	const [isAddFormOpen, setIsAddFormOpen] = useState(false)
	const [recipients, setRecipients] = useState<string[]>([])
	const [ccRecipients, setCcRecipients] = useState<string[]>([])
	const [emailDraft, setEmailDraft] = useState('')
	const [ccEmailDraft, setCcEmailDraft] = useState('')
	const [emailError, setEmailError] = useState('')
	const [ccEmailError, setCcEmailError] = useState('')
	const emailInputRef = useRef<HTMLInputElement | null>(null)
	const addFormRef = useRef<HTMLDivElement | null>(null)
	const [addFormHeight, setAddFormHeight] = useState<string>('0px')
	const closeRafRef = useRef<number | null>(null)
	const [animatedRowIds, setAnimatedRowIds] = useState<Set<string>>(new Set())
	const rowAnimationTimeoutRef = useRef<number | null>(null)

	useLayoutEffect(() => {
		const node = addFormRef.current
		if (!node) {
			return
		}

		const height = node.scrollHeight

		if (closeRafRef.current !== null) {
			cancelAnimationFrame(closeRafRef.current)
			closeRafRef.current = null
		}

		if (isAddFormOpen) {
			setAddFormHeight(`${height}px`)
			return
		}

		setAddFormHeight(`${height}px`)
		closeRafRef.current = requestAnimationFrame(() => {
			closeRafRef.current = requestAnimationFrame(() => {
				setAddFormHeight('0px')
			})
		})
	}, [isAddFormOpen])

	useEffect(() => {
		if (!data) {
			return
		}
		setSendingDisabled(Boolean(data.isSendingDisabled))
		setRecipients(data.recipients ?? [])
		setCcRecipients(data.ccRecipients ?? [])
		setEmailDraft('')
		setCcEmailDraft('')
		setEmailError('')
		setCcEmailError('')
		setIsAddFormOpen(false)
	}, [data])

	useEffect(() => {
		if (!isOpen) {
			setEmailDraft('')
			setCcEmailDraft('')
			setEmailError('')
			setCcEmailError('')
			setViewMode('table')
			setIsAddFormOpen(false)
		}
	}, [isOpen])

	useEffect(() => {
		return () => {
			if (rowAnimationTimeoutRef.current !== null) {
				window.clearTimeout(rowAnimationTimeoutRef.current)
			}
		}
	}, [])

	const focusEmailInput = () => {
		if (typeof requestAnimationFrame === 'function') {
			requestAnimationFrame(() => {
				emailInputRef.current?.focus()
			})
			return
		}
		setTimeout(() => {
			emailInputRef.current?.focus()
		}, 0)
	}

	const handleAddEmail = () => {
		setIsAddFormOpen(true)
		focusEmailInput()
	}

	const handleCancelAddEmail = () => {
		setEmailDraft('')
		setCcEmailDraft('')
		setEmailError('')
		setCcEmailError('')
		setIsAddFormOpen(false)
	}

	const handleConfirmAddEmail = () => {
		const toEmail = emailDraft.trim()
		const ccEmail = ccEmailDraft.trim()
		let hasError = false

		setEmailError('')
		setCcEmailError('')

		if (!toEmail && !ccEmail) {
			setEmailError('Indique pelo menos um email.')
			return
		}

		if (toEmail) {
			if (!isValidEmail(toEmail)) {
				setEmailError('Email inválido.')
				hasError = true
			} else if (recipients.some(item => item.toLowerCase() === toEmail.toLowerCase())) {
				setEmailError('Email já existe.')
				hasError = true
			}
		}

		if (ccEmail) {
			if (!isValidEmail(ccEmail)) {
				setCcEmailError('Email CC inválido.')
				hasError = true
			} else if (ccRecipients.some(item => item.toLowerCase() === ccEmail.toLowerCase())) {
				setCcEmailError('Email CC já existe.')
				hasError = true
			}
		}

		if (hasError) {
			return
		}

		const rowIdsToAnimate = new Set<string>()
		if (toEmail) {
			rowIdsToAnimate.add(`email-row-${recipients.length}`)
		}
		if (ccEmail) {
			rowIdsToAnimate.add(`email-row-${ccRecipients.length}`)
		}

		if (toEmail) {
			setRecipients(prev => [...prev, toEmail])
		}
		if (ccEmail) {
			setCcRecipients(prev => [...prev, ccEmail])
		}

		if (rowIdsToAnimate.size > 0) {
			const ids = Array.from(rowIdsToAnimate)
			setAnimatedRowIds(prev => {
				const next = new Set(prev)
				ids.forEach(id => next.add(id))
				return next
			})
			if (rowAnimationTimeoutRef.current !== null) {
				window.clearTimeout(rowAnimationTimeoutRef.current)
			}
			rowAnimationTimeoutRef.current = window.setTimeout(() => {
				setAnimatedRowIds(prev => {
					const next = new Set(prev)
					ids.forEach(id => next.delete(id))
					return next
				})
			}, 320)
		}

		setEmailDraft('')
		setCcEmailDraft('')
		setEmailError('')
		setCcEmailError('')
	}

	const handleSaveAction = () => {
		if (typeof handleSave === 'function') {
			handleSave({
				recipients,
				ccRecipients,
				isSendingDisabled: sendingDisabled,
			})
			return
		}
		console.log('Guardar emails da escala', recipients, ccRecipients)
	}

	if (!data) {
		return null
	}

	const hasRecipients = recipients.length > 0
	const hasCcRecipients = ccRecipients.length > 0
	const recipientRowsCount = Math.max(recipients.length, ccRecipients.length, 1)
	const emptyRecipientLabel = 'Sem destinatários'
	const emptyCcLabel = 'Sem destinatários CC'
	const placeholderTextSx = {
		...emailLineSx,
		color: '#9BB0D7',
		fontWeight: 500,
	}
	const tableRows: EmailRow[] = Array.from({ length: recipientRowsCount }).map((_item, index) => {
		const recipient = recipients[index]
		const ccRecipient = ccRecipients[index]
		const displayRecipient = recipient ?? (hasRecipients ? '-' : emptyRecipientLabel)
		const displayCc = ccRecipient ?? (hasCcRecipients ? '-' : emptyCcLabel)
		return {
			id: `email-row-${index}`,
			recipient: displayRecipient,
			ccRecipient: displayCc,
			isRecipientPlaceholder: !recipient,
			isCcPlaceholder: !ccRecipient,
		}
	})
	const gridColumns: GridColDef<EmailRow>[] = [
		{
			field: 'recipient',
			headerName: 'Destinatários',
			flex: 1,
			minWidth: 220,
			renderCell: (params: GridRenderCellParams<EmailRow, string>) => (
				<Typography sx={params.row.isRecipientPlaceholder ? placeholderTextSx : emailLineSx}>
					{params.value ?? ''}
				</Typography>
			),
		},
		{
			field: 'ccRecipient',
			headerName: 'Destinatários CC',
			flex: 1,
			minWidth: 220,
			renderCell: (params: GridRenderCellParams<EmailRow, string>) => (
				<Typography sx={params.row.isCcPlaceholder ? placeholderTextSx : emailLineSx}>{params.value ?? ''}</Typography>
			),
		},
	]

	return (
		<Dialog
			open={isOpen}
			onClose={closeModal}
			maxWidth={false}
			sx={{ pointerEvents: 'auto', zIndex: 2000 }}
			PaperProps={{
				sx: {
					width: 'min(1260px, 96vw)',
					height: 'min(640px, calc(100vh - 32px))',
					borderRadius: '4px',
					backgroundColor: '#FFFFFF',
					boxShadow: dialogShadow,
					overflow: 'hidden',
					zIndex: 2001,
				},
			}}
		>
			<Box
				sx={{
					fontFamily: 'Poppins, sans-serif',
					color: '#092056',
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<Box
					sx={{
						padding: '16px 24px 0',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: '16px',
					}}
				>
					<Typography
						sx={{
							fontSize: '16px',
							fontWeight: 600,
							lineHeight: 1.3,
							letterSpacing: '0.15px',
							color: '#092056',
						}}
					>
						Emails que recebem a escala
					</Typography>
					<IconButton
						aria-label='Fechar'
						onClick={closeModal}
						sx={{ padding: '4px' }}
					>
						<CloseRoundedIcon sx={{ fontSize: 20, color: '#00A5FF' }} />
					</IconButton>
				</Box>

				<Box
					sx={{
						padding: '14px 24px 0',
						display: 'grid',
						gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto' },
						alignItems: { xs: 'flex-start', md: 'center' },
						columnGap: '16px',
						rowGap: '12px',
					}}
				>
					<Typography
						sx={{
							fontSize: { xs: '24px', md: '42px' },
							fontWeight: 600,
							lineHeight: 1.15,
							letterSpacing: '0.15px',
							color: '#092056',
							minWidth: 0,
							whiteSpace: { xs: 'normal', md: 'nowrap' },
						}}
					>
						Emails que podem receber escala
					</Typography>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: '18px',
							flexWrap: { xs: 'wrap', md: 'nowrap' },
							justifyContent: { xs: 'flex-start', md: 'flex-end' },
							whiteSpace: { xs: 'normal', md: 'nowrap' },
						}}
					>
						<FormControlLabel
							control={
								<Checkbox
									size='small'
									checked={sendingDisabled}
									onChange={event => setSendingDisabled(event.target.checked)}
									sx={checkboxSx}
								/>
							}
							label='Processo de envio de email desativado'
							sx={checkboxLabelSx}
						/>
						<CustomButton
							type='button'
							variant='text'
							size='small'
							onClick={handleAddEmail}
							aria-expanded={isAddFormOpen}
							endIcon={<AddIcon sx={{ fontSize: '18px' }} />}
							sx={addLinkSx}
						>
							Adicionar Email
						</CustomButton>
						<ToggleButtonGroup
							exclusive
							value={viewMode}
							onChange={(_, value) => value && setViewMode(value)}
							sx={toggleGroupSx}
						>
							<ToggleButton
								value='table'
								aria-label='Tabela'
							>
								<TableRowsIcon sx={{ fontSize: '16px' }} />
							</ToggleButton>
							<ToggleButton
								value='list'
								aria-label='Lista'
							>
								<ViewHeadlineIcon sx={{ fontSize: '16px' }} />
							</ToggleButton>
						</ToggleButtonGroup>
					</Box>
				</Box>

				<Box sx={{ padding: '12px 24px 0' }}>
					<Box
						sx={{
							overflow: 'hidden',
							height: isAddFormOpen ? addFormHeight : '0px',
							opacity: isAddFormOpen ? 1 : 0,
							transform: isAddFormOpen ? 'translateY(0)' : 'translateY(-8px)',
							transition: getCollapseTransition(isAddFormOpen ? collapseEnterEasing : collapseExitEasing),
							willChange: 'height, opacity, transform',
							pointerEvents: isAddFormOpen ? 'auto' : 'none',
						}}
						onTransitionEnd={() => {
							if (isAddFormOpen) {
								setAddFormHeight('auto')
							}
						}}
					>
						<Box
							ref={addFormRef}
							sx={{
								backgroundColor: '#FBFDFF',
								borderRadius: '3px',
								padding: '18px 18px 14px',
								display: 'grid',
								gap: '12px',
							}}
						>
							<Typography
								sx={{
									fontSize: '20px',
									fontWeight: 600,
									lineHeight: 1.2,
									color: '#092056',
								}}
							>
								Destinatário
							</Typography>
							<Box
								sx={{
									display: 'grid',
									gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
									gap: '12px',
								}}
							>
								<TextField
									label='Destinatário'
									placeholder='email@example.com'
									value={emailDraft}
									onChange={event => {
										setEmailDraft(event.target.value)
										if (emailError) {
											setEmailError('')
										}
									}}
									onKeyDown={event => {
										if (event.key === 'Enter') {
											event.preventDefault()
											handleConfirmAddEmail()
										}
									}}
									inputRef={emailInputRef}
									error={Boolean(emailError)}
									helperText={emailError}
									InputLabelProps={{ shrink: true }}
									sx={outlinedFieldSx}
								/>
								<TextField
									label='Destinatário CC'
									placeholder='email@example.com'
									value={ccEmailDraft}
									onChange={event => {
										setCcEmailDraft(event.target.value)
										if (ccEmailError) {
											setCcEmailError('')
										}
									}}
									onKeyDown={event => {
										if (event.key === 'Enter') {
											event.preventDefault()
											handleConfirmAddEmail()
										}
									}}
									error={Boolean(ccEmailError)}
									helperText={ccEmailError}
									InputLabelProps={{ shrink: true }}
									sx={outlinedFieldSx}
								/>
							</Box>
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'flex-end',
									gap: '18px',
								}}
							>
								<CustomButton
									type='button'
									variant='text'
									size='small'
									onClick={handleCancelAddEmail}
									sx={inlineActionSx}
								>
									Cancelar
								</CustomButton>
								<CustomButton
									type='button'
									variant='text'
									size='small'
									onClick={handleConfirmAddEmail}
									sx={inlineActionSx}
								>
									Adicionar
								</CustomButton>
							</Box>
						</Box>
					</Box>
				</Box>

				<Box
					sx={{
						padding: '14px 24px 0',
						flex: 1,
						overflow: 'auto',
					}}
				>
					{viewMode === 'table' ? (
						<Box sx={{ height: '100%' }}>
							<AnimatedDataGridPro
								slots={{ columnMenu: CustomColumnMenu }}
								slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
								rows={tableRows}
								columns={gridColumns}
								density='compact'
								getRowClassName={params => {
									const classes = []
									if (params.indexRelativeToCurrentPage % 2 === 1) {
										classes.push('email-row-odd')
									}
									if (animatedRowIds.has(String(params.id))) {
										classes.push('email-row-enter')
									}
									return classes.join(' ')
								}}
								disableRowSelectionOnClick
								hideFooter
								rowHeight={36}
								columnHeaderHeight={38}
								sx={[tableSx, dataGridDensityTransitionSx]}
							/>
						</Box>
					) : (
						<Box
							sx={{
								display: 'grid',
								gap: '20px',
								gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
							}}
						>
							<Box>
								<Typography sx={listLabelSx}>Destinatários</Typography>
								<Box
									sx={{
										borderBottom: '1px solid rgba(155, 176, 215, 0.2)',
										marginTop: '8px',
										marginBottom: '8px',
									}}
								/>
								{hasRecipients ? (
									recipients.map((email, index) => (
										<Typography
											key={`${email}-${index}`}
											sx={emailLineSx}
										>
											{email}
										</Typography>
									))
								) : (
									<Typography sx={placeholderTextSx}>{emptyRecipientLabel}</Typography>
								)}
							</Box>

							<Box>
								<Typography sx={listLabelSx}>Destinatários CC</Typography>
								<Box
									sx={{
										borderBottom: '1px solid rgba(155, 176, 215, 0.2)',
										marginTop: '8px',
										marginBottom: '8px',
									}}
								/>
								{hasCcRecipients ? (
									ccRecipients.map((email, index) => (
										<Typography
											key={`${email}-${index}`}
											sx={emailLineSx}
										>
											{email}
										</Typography>
									))
								) : (
									<Typography sx={placeholderTextSx}>{emptyCcLabel}</Typography>
								)}
							</Box>
						</Box>
					)}
				</Box>

				<Box
					sx={{
						padding: '12px 24px 18px',
						display: 'flex',
						justifyContent: 'flex-end',
						gap: '10px',
					}}
				>
					<CustomButton
						variant='text'
						size='medium'
						onClick={closeModal}
						sx={popupSecondaryButtonSx}
					>
						Fechar
					</CustomButton>
					<CustomButton
						variant='contained'
						size='medium'
						onClick={handleSaveAction}
						sx={popupActionButtonSx}
					>
						Gravar
					</CustomButton>
				</Box>
			</Box>
		</Dialog>
	)
}

export default CustomModalScheduleEmails
